import { ReactNode, useEffect } from "react";
import { useScrollLock } from "../../Hooks/useScrollLock";

type ModalProps = {
  /** Called when the modal asks to close (Esc or backdrop click, when those are enabled). */
  onClose?: () => void;
  /**
   * Exact overlay/backdrop classes, passed through verbatim so each modal keeps its
   * own look. Defaults to the most common variant used across the app.
   */
  overlayClassName?: string;
  /** Close on the Escape key. Opt-in so adopting the component changes nothing. */
  closeOnEsc?: boolean;
  /** Close when the backdrop (the overlay itself, not its content) is clicked. Opt-in. */
  closeOnBackdropClick?: boolean;
  /** Keeps the page behind from scrolling while the modal is mounted. On unless a modal needs otherwise. */
  lockScroll?: boolean;
  children: ReactNode;
};

const DEFAULT_OVERLAY =
  "fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center";

/**
 * Shared modal shell. Renders the overlay and centres its content, and centralises the
 * Esc / backdrop-click / scroll-lock behaviours that were previously hand-rolled (and
 * drifted — `bg-opacity-40` vs `50`, differing flex order) across ~20 modals.
 *
 * Pass `overlayClassName` to preserve each modal's exact appearance. The page behind is
 * locked by default; Esc and backdrop click are opt-in.
 */
const Modal = ({
  onClose,
  overlayClassName = DEFAULT_OVERLAY,
  closeOnEsc = false,
  closeOnBackdropClick = false,
  lockScroll = true,
  children,
}: ModalProps) => {
  useEffect(() => {
    if (!closeOnEsc || !onClose) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEsc, onClose]);

  useScrollLock(lockScroll);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && onClose && event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={overlayClassName}
      onClick={closeOnBackdropClick ? handleOverlayClick : undefined}
    >
      {children}
    </div>
  );
};

export default Modal;
